// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../i18n';
import { Building2, Plus, X, Upload, Trash2, MailPlus, ShieldOff, ShieldCheck, Pencil } from 'lucide-react';
import {
  getLicenseOrgUnits,
  getLicenseInvoices, createLicenseInvoice, prepareLicenseInvoice,
  finalizeLicenseInvoice, cancelLicenseInvoice, downloadLicenseInvoicePdf,
  adminDeleteLicenseSeatData,
} from '../services/api';
// Même écran que le portail client, ciblé sur la licence désignée : Evalens
// monte l'organisation à la livraison du contrat, sans attendre que le client
// ait un administrateur de licence.
import OrganisationEchelons, { SelecteurEchelon } from '../components/OrganisationEchelons';
import { useLogoLicence } from '../lib/logoLicence';

const API =process.env.REACT_APP_API_URL || 'http://localhost:5000';
// ⛔ Plus de jeton (chantier ⑯) : la session part en cookie, via lib/session.js.
const auth = () => ({});

// `code` (le numéro de licence) est absent : il est attribué par le serveur à
// la création et n'est plus modifiable ensuite. `theme_code` l'est aussi —
// aucun générateur de rapport ne l'a jamais lu, le sélecteur ne pilotait rien.
const FIELD_DEFAULTS = {
  nom: '', contact_email: '',
  licence_annuelle: '',
  // Plus de mode « bloqué » (28/09/2026) : le dépassement est toujours facturé,
  // et le garde-fou (`plafond_depassement_pct`, 25 % par défaut) arrête un
  // emballement. `pool_overage_mode` / `ocr_overage_mode` ne sont plus envoyés.
  pool_analyses_count: '', pool_analyses_type: 'monthly', pool_overage_price: '',
  plafond_depassement_pct: 25,
  ocr_mode: 'disabled', ocr_monthly_cap: '', ocr_annual_cap: '', ocr_prix_page: '',
  seats_max: '', email_allowed_domains: '', mfa_required: false,
  date_debut: '', date_fin: '',
  can_see_users: true, can_see_stats: true,
  can_see_annotations: false, can_see_ratings: false,
  api_access: false,
  logo_placement: 'first_page',
  // `setup_fee_api` n'est plus saisi (28/09/2026) : aucune API n'existe, et
  // `utils/facturation.py` ne la facture plus. La colonne reste en base.
  setup_fee_onboarding: '',
  // Identité légale de facturation. Distincte de `nom`/`contact_email`, qui
  // servent l'usage courant : la raison sociale exacte diffère souvent du nom
  // commercial, et la facture part au service comptable, pas au chef de projet.
  // `qonto_client_id` est absent : il est posé par le serveur au premier
  // rapprochement, et le laisser saisir permettrait de rattacher les factures
  // d'une licence au client QONTO d'une autre.
  facturation_raison_sociale: '', facturation_siret: '', facturation_tva_intracom: '',
  facturation_adresse: '', facturation_code_postal: '', facturation_ville: '',
  facturation_pays: 'FR', facturation_email: '',
  facturation_frequence: 'annual', facturation_tva_taux: '20',
  facturation_majoration_taux: '',
};

function LicenseForm({ initial, onSave, onCancel, saving }) {
  const { t } = useI18n();
  const [fields, setFields] = useState({ ...FIELD_DEFAULTS, ...initial });
  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));

  // ⚠️ Un Cabinet se règle par son forfait et son abonnement Stripe : le
  // formulaire n'en montre que ce qui reste modifiable à la main. Le serveur
  // refuse le reste (`CHAMPS_LICENCE_CABINET_MODIFIABLES`) — ce formulaire
  // réduit évite seulement de proposer des réglages qui seraient refusés.
  if (initial?.est_cabinet) {
    const champStyle = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' };
    const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 };
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
        <div style={{ gridColumn: 'span 2', padding: '10px 14px', borderRadius: 8, background: '#e0f2fe', color: '#0369a1', fontSize: 13 }}>
          {t('ent_cabinet_verrouille')}
        </div>
        <div>
          <label style={labelStyle}>{t('ent_form_contact_email')}</label>
          <input type="email" value={fields.contact_email ?? ''} onChange={e => set('contact_email', e.target.value)} style={champStyle} />
        </div>
        <div>
          <label style={labelStyle}>{t('ent_form_email_domains')}</label>
          <input type="text" value={fields.email_allowed_domains ?? ''} onChange={e => set('email_allowed_domains', e.target.value)} style={champStyle} />
        </div>
        <label style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={!!fields.mfa_required} onChange={e => set('mfa_required', e.target.checked)} />
          {t('ent_form_mfa_required')}
        </label>
        <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button onClick={onCancel} disabled={saving}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
            {t('cancel')}
          </button>
          <button
            onClick={() => onSave({
              contact_email: fields.contact_email ?? '',
              email_allowed_domains: fields.email_allowed_domains ?? '',
              mfa_required: !!fields.mfa_required,
            })}
            disabled={saving}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {saving ? t('ent_saving') : t('save')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
      {/* Numéro de licence — attribué par le serveur, jamais saisi. Absent tant
          que la licence n'existe pas : il n'est connu qu'après création. */}
      {initial?.code && (
        <div style={{ gridColumn: 'span 2' }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_numero')}</label>
          <div style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-subtle)', color: 'var(--text-muted)', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
            {initial.code}
          </div>
        </div>
      )}

      {/* Identité */}
      {[
        ['nom', t('ent_form_nom'), 'text', true],
        ['contact_email', t('ent_form_contact_email'), 'email', false],
        ['email_allowed_domains', t('ent_form_email_domains'), 'text', false],
        ['date_debut', t('ent_form_date_debut'), 'date', false],
        ['date_fin', t('ent_form_date_fin'), 'date', false],
      ].map(([k, label, type, required]) => (
        <div key={k}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{label}{required && ' *'}</label>
          <input type={type} value={fields[k]} onChange={e => set(k, e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        </div>
      ))}

      {/* Tarification */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_licence_annuelle')}</label>
        <input type="number" min="0" step="0.01" value={fields.licence_annuelle} onChange={e => set('licence_annuelle', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
      </div>

      {/* Pool */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_pool_count')}</label>
        <input type="number" min="0" value={fields.pool_analyses_count} onChange={e => set('pool_analyses_count', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_pool_type')}</label>
        <select value={fields.pool_analyses_type} onChange={e => set('pool_analyses_type', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }}>
          <option value="monthly">{t('ent_monthly')}</option>
          <option value="annual">{t('ent_annual')}</option>
        </select>
      </div>
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_overage_price')}</label>
        <input type="number" min="0" step="0.01" value={fields.pool_overage_price} onChange={e => set('pool_overage_price', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_garde_fou')}</label>
        <input type="number" min="0" step="1" value={fields.plafond_depassement_pct ?? ''} onChange={e => set('plafond_depassement_pct', e.target.value)}
          placeholder={t('ent_garde_fou_aucun')}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_seats_max')}</label>
        <input type="number" min="0" value={fields.seats_max} onChange={e => set('seats_max', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
      </div>

      {/* OCR */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_ocr_mode')}</label>
        <select value={fields.ocr_mode} onChange={e => set('ocr_mode', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }}>
          <option value="disabled">{t('ent_ocr_disabled')}</option>
          <option value="monthly">{t('ent_ocr_monthly')}</option>
          <option value="annual">{t('ent_ocr_annual')}</option>
          <option value="per_page">{t('ent_ocr_per_page')}</option>
        </select>
      </div>
      {fields.ocr_mode === 'monthly' && (
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_ocr_pool_cap')}</label>
          <input type="number" min="0" value={fields.ocr_monthly_cap} onChange={e => set('ocr_monthly_cap', e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        </div>
      )}
      {fields.ocr_mode === 'annual' && (
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_ocr_annual_cap')}</label>
          <input type="number" min="0" value={fields.ocr_annual_cap ?? ''} onChange={e => set('ocr_annual_cap', e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        </div>
      )}
      {fields.ocr_mode !== 'disabled' && (
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
            {fields.ocr_mode === 'per_page' ? t('ent_form_ocr_page_price') : t('ent_form_ocr_overage_price')}
          </label>
          <input type="number" min="0" step="0.001" value={fields.ocr_prix_page} onChange={e => set('ocr_prix_page', e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        </div>
      )}
      {/* Setup (frais one-shot) */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_form_setup_onboarding')}</label>
        <input type="number" min="0" step="0.01" value={fields.setup_fee_onboarding} onChange={e => set('setup_fee_onboarding', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
      </div>

      {/* Options booléennes */}
      <div style={{ gridColumn: 'span 2', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {[
          ['mfa_required', t('ent_form_mfa_required')],
          ['can_see_users', 'Voir utilisateurs'],
          ['can_see_stats', 'Voir statistiques'],
          ['can_see_annotations', 'Voir annotations'],
          ['can_see_ratings', 'Voir évaluations'],
          ['api_access', 'Accès API'],
        ].map(([k, label]) => (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={!!fields[k]} onChange={e => set(k, e.target.checked)} />
            {label}
          </label>
        ))}
      </div>

      {/* Identité légale de facturation — ce qui figure sur la facture émise */}
      <div style={{ gridColumn: 'span 2', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gray)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>{t('ent_fact_section')}</div>
      </div>
      {[
        ['facturation_raison_sociale', t('ent_fact_raison_sociale'), 'text'],
        ['facturation_email', t('ent_fact_email'), 'email'],
        ['facturation_siret', t('ent_fact_siret'), 'text'],
        ['facturation_tva_intracom', t('ent_fact_tva'), 'text'],
        ['facturation_adresse', t('ent_fact_adresse'), 'text'],
        ['facturation_code_postal', t('ent_fact_cp'), 'text'],
        ['facturation_ville', t('ent_fact_ville'), 'text'],
        ['facturation_pays', t('ent_fact_pays'), 'text'],
      ].map(([k, label, type]) => (
        <div key={k}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{label}</label>
          <input type={type} value={fields[k] ?? ''} onChange={e => set(k, e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        </div>
      ))}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_fact_frequence')}</label>
        <select value={fields.facturation_frequence} onChange={e => set('facturation_frequence', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }}>
          <option value="annual">{t('ent_fact_freq_annual')}</option>
          <option value="quarterly">{t('ent_fact_freq_quarterly')}</option>
          <option value="monthly">{t('ent_fact_freq_monthly')}</option>
        </select>
      </div>
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_fact_tva_taux')}</label>
        <input type="number" min="0" max="100" step="0.1" value={fields.facturation_tva_taux ?? ''} onChange={e => set('facturation_tva_taux', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
      </div>
      <div style={{ gridColumn: 'span 2' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('ent_fact_majoration')}</label>
        <input type="number" min="0" max="100" step="0.1" value={fields.facturation_majoration_taux ?? ''} onChange={e => set('facturation_majoration_taux', e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{t('ent_fact_aide_majoration')}</div>
      </div>

      {/* Actions */}
      <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
        <button onClick={onCancel} disabled={saving}
          style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
          {t('cancel')}
        </button>
        <button
          onClick={() => {
            const payload = { ...fields };
            if (payload.pool_analyses_count === '') delete payload.pool_analyses_count;
            else payload.pool_analyses_count = parseInt(payload.pool_analyses_count, 10);
            if (payload.seats_max === '') payload.seats_max = null;
            else payload.seats_max = parseInt(payload.seats_max, 10);
            if (payload.ocr_monthly_cap === '') delete payload.ocr_monthly_cap;
            else payload.ocr_monthly_cap = parseInt(payload.ocr_monthly_cap, 10);
            if (payload.ocr_annual_cap === '' || payload.ocr_annual_cap == null) delete payload.ocr_annual_cap;
            else payload.ocr_annual_cap = parseInt(payload.ocr_annual_cap, 10);
            // ⚠️ Vide = AUCUN garde-fou, envoyé en `null` explicite (et non
            // supprimé du corps) : sinon on ne pourrait jamais le retirer.
            if (payload.plafond_depassement_pct === '' || payload.plafond_depassement_pct == null) payload.plafond_depassement_pct = null;
            else payload.plafond_depassement_pct = parseFloat(payload.plafond_depassement_pct);
            ['pool_overage_price', 'ocr_prix_page', 'licence_annuelle', 'setup_fee_onboarding'].forEach(k => {
              if (payload[k] === '') delete payload[k];
              else payload[k] = parseFloat(payload[k]);
            });
            // ⚠️ Les taux de facturation partent en `null` quand ils sont vidés,
            // JAMAIS supprimés du corps ni laissés en chaîne vide. Les supprimer
            // rendrait impossible d'effacer une majoration négociée ; une chaîne
            // vide serait refusée par PostgreSQL sur une colonne REAL.
            // `null` porte ici un sens métier : « appliquer le tarif standard ».
            ['facturation_tva_taux', 'facturation_majoration_taux'].forEach(k => {
              payload[k] = (payload[k] === '' || payload[k] == null) ? null : parseFloat(payload[k]);
            });
            onSave(payload);
          }}
          disabled={saving || !fields.nom.trim()}
          style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {saving ? t('ent_saving') : t('save')}
        </button>
      </div>
    </div>
  );
}

function InviteModal({ licenseId, orgUnits, onClose, onSent }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  // Rattachement décidé DÈS l'invitation : c'est tout l'intérêt de monter
  // l'organisation avant d'inviter — sinon chaque invité arrive hors
  // organisation et doit être rangé à la main après son acceptation.
  const [orgUnitId, setOrgUnitId] = useState('');
  const [rights, setRights] = useState({ lic_ocr_enabled: false, lic_projects_enabled: false, lic_annotations_enabled: false, lic_ratings_enabled: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setError('');
    setSending(true);
    try {
      const r = await fetch(`${API}/api/enterprise/licenses/${licenseId}/invitations`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, license_role: role, org_unit_id: orgUnitId || null, ...rights }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Erreur'); setSending(false); return; }
      onSent();
      onClose();
    } catch (e) { setError('Erreur réseau'); setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t('ent_invite_user')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid #ef4444', borderRadius: 6, padding: '8px 12px', color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="user@entreprise.com"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Rôle</label>
          <select value={role} onChange={e => setRole(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }}>
            <option value="member">{t('ent_role_member')}</option>
            <option value="license_admin">{t('ent_role_admin')}</option>
          </select>
        </div>
        {(orgUnits || []).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('portal_seat_org')}</label>
            <SelecteurEchelon orgUnits={orgUnits} value={orgUnitId} onChange={setOrgUnitId}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }} />
          </div>
        )}
        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[['lic_ocr_enabled', t('ent_rights_ocr')], ['lic_projects_enabled', t('ent_rights_projects')], ['lic_annotations_enabled', t('ent_rights_annotations')], ['lic_ratings_enabled', t('ent_rights_ratings')]].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={rights[k]} onChange={e => setRights(r => ({ ...r, [k]: e.target.checked }))} />{label}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>{t('cancel')}</button>
          <button onClick={send} disabled={sending || !email.trim()}
            style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {sending ? '…' : t('ent_invite_user')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminEnterpriseClients() {
  const { t } = useI18n();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);       // license detail
  // Logo chargé en `fetch` + en-tête : jamais de `?token=` dans une `<img src>`.
  const logoSelection = useLogoLicence(selected?.logo_path);
  const [detailTab, setDetailTab] = useState('infos');  // infos | seats | org | invitations
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  // Échelons de la licence sélectionnée : chargés ici parce que le formulaire
  // d'invitation en a besoin pour proposer un rattachement, même quand
  // l'onglet Organisation n'a jamais été ouvert.
  const [orgUnits, setOrgUnits] = useState([]);
  // Chargées à l'ouverture de la fiche, comme les échelons : l'onglet Factures
  // doit pouvoir s'afficher rempli dès le premier clic.
  const [invoices, setInvoices] = useState([]);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);       // create / edit form
  const [editData, setEditData] = useState(null);        // null = create
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editSeat, setEditSeat] = useState(null);
  // Effacement RGPD d'un siège : le siège visé, la phrase saisie, l'état.
  const [purgeSeat, setPurgeSeat]       = useState(null);
  const [purgeText, setPurgeText]       = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeError, setPurgeError]     = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/enterprise-licenses`, { headers: auth() });
      const d = await r.json();
      setLicenses(d.licenses || []);
    } catch (e) { setError('Erreur chargement'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadOrgUnits = useCallback(async (licenseId) => {
    try {
      setOrgUnits((await getLicenseOrgUnits(licenseId)).org_units || []);
    } catch (e) { setOrgUnits([]); }
  }, []);

  const loadInvoices = useCallback(async (licenseId) => {
    try {
      setInvoices((await getLicenseInvoices(licenseId)).invoices || []);
    } catch (e) { setInvoices([]); }
  }, []);

  // Traduit le `error_code` du serveur en message métier. Sans ce détour,
  // « rien à facturer » — un cas parfaitement normal — s'afficherait comme une
  // erreur brute et ferait chercher une panne inexistante.
  const messageFacture = (e) => ({
    rien_a_facturer:       t('ent_facture_nothing_to_bill'),
    periode_deja_couverte: t('ent_facture_already_covered'),
    qonto_absent:          t('ent_facture_qonto_missing'),
    pdf_en_attente:        t('ent_facture_pdf_pending'),
    non_prepare:           t('ent_facture_not_prepared'),
    deja_prepare:          t('ent_facture_already_prepared'),
  }[e?.code] || e?.message || t('error'));

  // Un brouillon dont l'identifiant QONTO est renseigné EST un proforma prêt :
  // l'état existe déjà en base, aucune colonne supplémentaire n'a été nécessaire.
  const estProforma = (f) => f.statut === 'draft' && !!f.qonto_invoice_id;

  const handlePrepareInvoice = async (facture) => {
    setInvoiceBusy(true);
    setError('');
    try {
      await prepareLicenseInvoice(facture.id);
      await loadInvoices(selected.id);
    } catch (e) { setError(messageFacture(e)); }
    setInvoiceBusy(false);
  };

  const handleCreateInvoice = async (type) => {
    setInvoiceBusy(true);
    setError('');
    try {
      await createLicenseInvoice(selected.id, type);
      await loadInvoices(selected.id);
    } catch (e) { setError(messageFacture(e)); }
    setInvoiceBusy(false);
  };

  // ⚠️ Confirmation OBLIGATOIRE et explicite : `finalize` attribue un numéro de
  // la séquence légale continue. Une facture émise par erreur ne se supprime
  // pas, elle demande un avoir. Le montant et le destinataire sont rappelés
  // dans le message pour que la relecture porte sur autre chose qu'un « OK ».
  const handleFinalizeInvoice = async (facture) => {
    const client = facture.client_snapshot?.raison_sociale || selected?.nom || '—';
    const message = t('ent_facture_confirm_send')
      .replace('{ttc}', `${facture.montant_ttc.toFixed(2)} €`)
      .replace('{client}', client);
    if (!window.confirm(message)) return;
    setInvoiceBusy(true);
    setError('');
    try {
      await finalizeLicenseInvoice(facture.id);
      await loadInvoices(selected.id);
    } catch (e) { setError(messageFacture(e)); }
    setInvoiceBusy(false);
  };

  const handleCancelInvoice = async (facture) => {
    if (!window.confirm(t('ent_facture_confirm_cancel'))) return;
    setInvoiceBusy(true);
    try {
      await cancelLicenseInvoice(facture.id, '');
      await loadInvoices(selected.id);
    } catch (e) { setError(messageFacture(e)); }
    setInvoiceBusy(false);
  };

  const handleDownloadInvoice = async (facture) => {
    try {
      await downloadLicenseInvoicePdf(facture.id, facture.qonto_invoice_number);
    } catch (e) { setError(messageFacture(e)); }
  };

  // `reinitialiserOnglet` : vrai quand on SÉLECTIONNE une licence dans la liste
  // (on repart des Informations), faux quand on RECHARGE la fiche courante après
  // une action. Sans cette distinction, révoquer ou réactiver un siège depuis
  // l'onglet Sièges renvoyait l'écran sur Informations — il fallait revenir à
  // l'onglet à chaque geste (signalé par Marc le 29/07/2026).
  const loadDetail = async (lic, { reinitialiserOnglet = true } = {}) => {
    setSelected(lic);
    if (reinitialiserOnglet) setDetailTab('infos');
    setOrgUnits([]);
    setInvoices([]);
    const r = await fetch(`${API}/api/admin/enterprise-licenses/${lic.id}/users`, { headers: auth() });
    if (r.ok) {
      const d = await r.json();
      setUsers(d.users || []);
      setInvitations(d.invitations || []);
    }
    loadOrgUnits(lic.id);
    loadInvoices(lic.id);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    setError('');
    try {
      const url = editData ? `${API}/api/admin/enterprise-licenses/${editData.id}` : `${API}/api/admin/enterprise-licenses`;
      const method = editData ? 'PATCH' : 'POST';
      const r = await fetch(url, {
        method,
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Erreur'); setSaving(false); return; }
      setShowForm(false);
      setEditData(null);
      await load();
      if (editData) setSelected(d.license);
    } catch (e) { setError('Erreur réseau'); }
    setSaving(false);
  };

  const handleDeactivate = async (lic) => {
    if (!window.confirm(t('ent_confirm_deactivate'))) return;
    await fetch(`${API}/api/admin/enterprise-licenses/${lic.id}`, { method: 'DELETE', headers: auth() });
    await load();
    const r2 = await fetch(`${API}/api/admin/enterprise-licenses/${lic.id}`, { headers: auth() });
    if (r2.ok) { const d = await r2.json(); setSelected(d.license); }
  };

  const handleActivate = async (lic) => {
    if (!window.confirm(t('ent_confirm_activate'))) return;
    const r = await fetch(`${API}/api/admin/enterprise-licenses/${lic.id}/activate`, { method: 'POST', headers: auth() });
    if (r.ok) { const d = await r.json(); setSelected(d.license); await load(); }
    else { const d = await r.json(); setError(d.error || 'Erreur réactivation'); }
  };

  const handleHardDelete = async (lic) => {
    if (!window.confirm(t('ent_confirm_hard_delete'))) return;
    const r = await fetch(`${API}/api/admin/enterprise-licenses/${lic.id}/hard-delete`, { method: 'DELETE', headers: auth() });
    if (r.ok) { setSelected(null); await load(); }
    else { const d = await r.json(); setError(d.error || 'Erreur suppression'); }
  };

  const handleLogoUpload = async (licId) => {
    if (!logoFile) return;
    setLogoUploading(true);
    const fd = new FormData();
    fd.append('logo', logoFile);
    const r = await fetch(`${API}/api/admin/enterprise-licenses/${licId}/logo`, { method: 'POST', headers: auth(), body: fd });
    if (r.ok) {
      const d = await r.json();
      setSelected(s => ({ ...s, logo_path: d.logo_path }));
      setLogoFile(null);
      await load();
    } else {
      const d = await r.json();
      setError(d.error || 'Erreur upload logo');
    }
    setLogoUploading(false);
  };

  const handleLogoDelete = async (licId) => {
    await fetch(`${API}/api/admin/enterprise-licenses/${licId}/logo`, { method: 'DELETE', headers: auth() });
    setSelected(s => ({ ...s, logo_path: null }));
    await load();
  };

  // Rechargement de la fiche courante SANS revenir à l'onglet Informations.
  const rafraichirDetail = () => loadDetail(selected, { reinitialiserOnglet: false });

  const handleRevoke = async (userId) => {
    if (!window.confirm(t('portal_revoke_confirm'))) return;
    await fetch(`${API}/api/admin/enterprise-licenses/${selected.id}/users/${userId}/revoke`, { method: 'POST', headers: auth() });
    await rafraichirDetail();
  };

  // Effacement RGPD d'un siège — Evalens uniquement, sur demande écrite de
  // l'entreprise. Volontairement PAS un `window.confirm` comme la révocation :
  // celle-ci ne détruit rien et se défait d'un clic, celle-là est définitive.
  // La phrase saisie est aussi ce que le serveur exige — un écran qui
  // confirmerait plus facilement que la route ne servirait à rien.
  const handlePurgeSeat = async () => {
    if (!purgeSeat || purgeText !== t('ent_seat_purge_phrase')) return;
    setPurgeLoading(true); setPurgeError('');
    try {
      await adminDeleteLicenseSeatData(selected.id, purgeSeat.id);
      setPurgeSeat(null); setPurgeText('');
      await rafraichirDetail();
    } catch (e) { setPurgeError(e.message); }
    finally { setPurgeLoading(false); }
  };

  // Réactiver rend l'accès à l'application : c'est aussi conséquent que de le
  // retirer, et cela consomme un siège. La confirmation manquait (29/07/2026).
  const handleRestore = async (userId) => {
    if (!window.confirm(t('portal_restore_confirm'))) return;
    await fetch(`${API}/api/admin/enterprise-licenses/${selected.id}/users/${userId}/restore`, { method: 'POST', headers: auth() });
    await rafraichirDetail();
  };

  const handleUpdateSeat = async (userId, updates) => {
    const r = await fetch(`${API}/api/admin/enterprise-licenses/${selected.id}/users/${userId}`, {
      method: 'PATCH',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (r.ok) { setEditSeat(null); await rafraichirDetail(); }
    else { const d = await r.json(); setError(d.error || 'Erreur modification siège'); }
  };

  const handleCancelInvite = async (invId) => {
    if (!window.confirm(t('ent_cancel_invite_confirm'))) return;
    await fetch(`${API}/api/enterprise/licenses/${selected.id}/invitations/${invId}`, { method: 'DELETE', headers: auth() });
    await rafraichirDetail();
  };

  const badgeStyle = (active) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    background: active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
    color: active ? '#059669' : '#ef4444',
  });
  // Même bleu que le badge de palier dans Utilisateurs et Rentabilité.
  const badgeCabinet = {
    display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    background: '#e0f2fe', color: '#0369a1',
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Liste */}
      <div style={{ width: 320, minWidth: 280, borderRight: '1px solid var(--gray)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={18} style={{ color: 'var(--blue)' }} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>{t('ent_page_title')}</span>
          </div>
          <button onClick={() => { setEditData(null); setShowForm(true); }}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            <Plus size={14} />{t('ent_create')}
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {loading && <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</div>}
          {!loading && licenses.length === 0 && <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>{t('ent_list_empty')}</div>}
          {licenses.map(lic => (
            <div key={lic.id} onClick={() => loadDetail(lic)}
              style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, background: selected?.id === lic.id ? 'rgba(59,130,246,0.08)' : 'transparent', border: selected?.id === lic.id ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent' }}
              onMouseEnter={e => { if (selected?.id !== lic.id) e.currentTarget.style.background = 'rgba(128,128,128,0.06)'; }}
              onMouseLeave={e => { if (selected?.id !== lic.id) e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--black)' }}>{lic.code}</span>
                <span style={{ display: 'flex', gap: 4 }}>
                  {lic.est_cabinet && <span style={badgeCabinet}>{t('ent_badge_cabinet')}</span>}
                  <span style={badgeStyle(lic.actif)}>{lic.actif ? t('ent_active_label') : t('ent_inactive_label')}</span>
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{lic.nom}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                {lic.seats_max != null ? `${lic.seats_active || 0}/${lic.seats_max} sièges` : `${lic.seats_active || 0} sièges`}
                {lic.date_fin && ` · fin ${lic.date_fin}`}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Détail / Formulaire */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            {error}<button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><X size={14} /></button>
          </div>
        )}

        {/* Formulaire création / édition */}
        {showForm && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{editData ? t('ent_edit') : t('ent_create')}</div>
            <LicenseForm initial={editData || {}} onSave={handleSave} onCancel={() => { setShowForm(false); setEditData(null); }} saving={saving} />
          </div>
        )}

        {/* Détail d'une licence */}
        {!showForm && selected && (
          <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18, fontWeight: 700 }}>{selected.nom}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: 'var(--text-muted)', background: 'var(--gray)', padding: '2px 8px', borderRadius: 4 }}>{selected.code}</span>
                  {selected.est_cabinet && <span style={badgeCabinet}>{t('ent_badge_cabinet')}</span>}
                  <span style={badgeStyle(selected.actif)}>{selected.actif ? t('ent_active_label') : t('ent_inactive_label')}</span>
                </div>
                {selected.contact_email && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{selected.contact_email}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setEditData(selected); setShowForm(true); }}
                  style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('ent_edit')}</button>
                {/* Un Cabinet s'active et se ferme avec son abonnement Stripe :
                    le serveur refuse de le faire à la main (`_refus_activation_cabinet`).
                    Seule la suppression d'un Cabinet déjà fermé reste proposée. */}
                {selected.est_cabinet ? (
                  !selected.actif && (
                    <button onClick={() => handleHardDelete(selected)}
                      style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'white' }}>{t('ent_delete')}</button>
                  )
                ) : !!selected.actif ? (
                  <button onClick={() => handleDeactivate(selected)}
                    style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #ef4444', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#ef4444' }}>{t('ent_deactivate')}</button>
                ) : (
                  <>
                    <button onClick={() => handleActivate(selected)}
                      style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #059669', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#059669' }}>{t('ent_activate')}</button>
                    <button onClick={() => handleHardDelete(selected)}
                      style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'white' }}>{t('ent_delete')}</button>
                  </>
                )}
              </div>
            </div>

            {/* Onglets */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--gray)', marginBottom: 20, gap: 0 }}>
              {/* Pas d'onglet Organisation pour un Cabinet : échelons réservés à
                  l'Entreprise (CGV 1.4, art. 3.1 — `_refus_echelons_cabinet`). */}
              {[['infos', t('ent_tab_infos')], ['seats', t('ent_tab_seats')],
                ...(selected.est_cabinet ? [] : [['org', t('portal_tab_org')]]),
                ['invitations', t('ent_tab_invitations')],
                ['invoices', t('ent_tab_invoices')]].map(([k, label]) => (
                <button key={k} onClick={() => setDetailTab(k)}
                  style={{ padding: '8px 18px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: detailTab === k ? 700 : 400, color: detailTab === k ? 'var(--blue)' : 'var(--black)', borderBottom: `2px solid ${detailTab === k ? 'var(--blue)' : 'transparent'}`, marginBottom: -1 }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Infos */}
            {detailTab === 'infos' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  [t('ent_form_licence_annuelle'), selected.licence_annuelle != null ? `${selected.licence_annuelle}€` : '—'],
                  ['Pool analyses', selected.pool_analyses_count != null ? `${selected.pool_analyses_count} / ${selected.pool_analyses_type === 'annual' ? t('ent_annual') : t('ent_monthly')}` : '—'],
                  [t('ent_form_overage_price'), selected.pool_overage_price != null ? `${selected.pool_overage_price}€` : '—'],
                  [t('ent_form_garde_fou'), selected.plafond_depassement_pct != null ? `${selected.plafond_depassement_pct} %` : t('ent_garde_fou_aucun')],
                  ['OCR', selected.ocr_mode === 'disabled' ? t('ent_ocr_disabled')
                    : ['monthly', 'annual'].includes(selected.ocr_mode)
                      ? `${t(selected.ocr_mode === 'annual' ? 'ent_ocr_annual' : 'ent_ocr_monthly')} (${(selected.ocr_mode === 'annual' ? selected.ocr_annual_cap : selected.ocr_monthly_cap) ?? '—'} p. · ${t('ent_form_ocr_overage_price')} ${selected.ocr_prix_page != null ? selected.ocr_prix_page + '€/p' : '—'})`
                      : `${t('ent_ocr_per_page')} (${selected.ocr_prix_page != null ? selected.ocr_prix_page + '€/p' : '—'})`],
                  ['Sièges max', selected.seats_max != null ? selected.seats_max : t('ent_seats_unlimited')],
                  ['Domaines autorisés', selected.email_allowed_domains || '—'],
                  ['MFA obligatoire', selected.mfa_required ? 'Oui' : 'Non'],
                  ['Début', selected.date_debut || '—'],
                  ['Fin', selected.date_fin || '—'],
                  [t('ent_form_setup_onboarding'), selected.setup_fee_onboarding ? `${selected.setup_fee_onboarding}€` : '—'],
                ].map(([label, val]) => (
                  <div key={label} style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8, border: '1px solid var(--gray)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{val}</div>
                  </div>
                ))}

                {/* Options booléennes */}
                <div style={{ gridColumn: 'span 2', padding: '12px 14px', background: 'var(--bg-input)', borderRadius: 8, border: '1px solid var(--gray)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600 }}>Options activées</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px' }}>
                    {[
                      [selected.can_see_users, 'Voir utilisateurs'],
                      [selected.can_see_stats, 'Voir statistiques'],
                      [selected.can_see_annotations, 'Voir annotations'],
                      [selected.can_see_ratings, 'Voir évaluations'],
                      [selected.api_access, 'Accès API'],
                    ].map(([val, label]) => (
                      <span key={label} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, color: val ? 'var(--black)' : 'var(--text-muted)' }}>
                        <span style={{ color: val ? '#059669' : '#ef4444', fontWeight: 700, fontSize: 13 }}>{val ? '✓' : '✗'}</span>{label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Logo */}
                <div style={{ gridColumn: 'span 2', padding: '14px 16px', background: 'var(--bg-input)', borderRadius: 8, border: '1px solid var(--gray)' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>Logo</div>
                  {selected.logo_path && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      {logoSelection && (
                        <img src={logoSelection}
                          alt="Logo" style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--gray)' }} />
                      )}
                      <button onClick={() => handleLogoDelete(selected.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 6, border: '1px solid #ef4444', background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#ef4444' }}>
                        <Trash2 size={12} />{t('ent_delete_logo')}
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setLogoFile(e.target.files[0])}
                      style={{ fontSize: 12 }} />
                    {logoFile && (
                      <button onClick={() => handleLogoUpload(selected.id)} disabled={logoUploading}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 12 }}>
                        <Upload size={12} />{logoUploading ? '…' : t('ent_upload_logo')}
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{t('ent_logo_hint')}</div>
                </div>
              </div>
            )}

            {/* Sièges */}
            {detailTab === 'seats' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button onClick={() => setShowInvite(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <MailPlus size={14} />{t('ent_invite_user')}
                  </button>
                </div>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--gray)' }}>
                      {['Email', 'Rôle', t('ent_rights_ocr'), t('ent_rights_projects'), t('ent_rights_annotations'), t('ent_rights_ratings'), 'Statut', ''].map((h, i) => (
                        <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 && (
                      <tr><td colSpan={8} style={{ padding: '20px 10px', color: 'var(--text-muted)', textAlign: 'center' }}>{t('portal_no_seats')}</td></tr>
                    )}
                    {users.map(u => (
                      <tr key={u.id} style={{ borderBottom: '1px solid var(--gray)' }}>
                        <td style={{ padding: '8px 10px' }}>{u.email}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{u.license_role === 'license_admin' ? t('ent_role_admin') : t('ent_role_member')}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{u.lic_ocr_enabled ? '✓' : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{u.lic_projects_enabled ? '✓' : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{u.lic_annotations_enabled ? '✓' : '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{u.lic_ratings_enabled ? '✓' : '—'}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={badgeStyle(u.is_active)}>{u.is_active ? t('ent_seats_active') : t('ent_seats_revoked')}</span>
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button onClick={() => setEditSeat(u)}
                              style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 11 }}>
                              <Pencil size={10} />{t('ent_edit')}
                            </button>
                            {u.is_active ? (
                              <button onClick={() => handleRevoke(u.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid #ef4444', background: 'transparent', cursor: 'pointer', fontSize: 11, color: '#ef4444' }}>
                                <ShieldOff size={10} />
                              </button>
                            ) : (
                              <button onClick={() => handleRestore(u.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid #059669', background: 'transparent', cursor: 'pointer', fontSize: 11, color: '#059669' }}>
                                <ShieldCheck size={10} />
                              </button>
                            )}
                            {/* Effacement RGPD — Evalens uniquement, sur demande
                                écrite. Un license_admin n'a pas cette route (403). */}
                            <button onClick={() => { setPurgeSeat(u); setPurgeText(''); setPurgeError(''); }}
                              title={t('ent_seat_purge_title')}
                              style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 4, border: '1px solid #b91c1c', background: 'transparent', cursor: 'pointer', fontSize: 11, color: '#b91c1c' }}>
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Confirmation d'effacement — pas un `window.confirm` comme la
                    révocation : celle-ci ne détruit rien et se défait d'un clic,
                    celle-là est définitive. La phrase exigée est la MÊME que
                    celle du serveur : un écran qui confirmerait plus facilement
                    que la route ne protégerait personne. */}
                {purgeSeat && (
                  <div style={{ marginTop: 16, padding: '16px 18px', borderRadius: 8, border: '2px solid #fecaca', background: 'var(--bg-surface)' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', marginBottom: 8 }}>
                      {t('ent_seat_purge_title')} — {purgeSeat.email}
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {t('ent_seat_purge_intro')}
                    </p>
                    <p style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600, marginBottom: 12 }}>
                      {t('ent_seat_purge_irreversible')}
                    </p>
                    <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                      {t('ent_seat_purge_confirm_label')} <strong>{t('ent_seat_purge_phrase')}</strong> {t('ent_seat_purge_confirm_hint')}
                    </label>
                    <input value={purgeText} onChange={e => setPurgeText(e.target.value)}
                      placeholder={t('ent_seat_purge_phrase')}
                      style={{ width: '100%', maxWidth: 320, padding: '7px 10px', borderRadius: 6, border: '1px solid #fecaca', fontSize: 13, boxSizing: 'border-box' }} />
                    {purgeError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>❌ {purgeError}</p>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={() => { setPurgeSeat(null); setPurgeText(''); setPurgeError(''); }}
                        style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>
                        {t('cancel')}
                      </button>
                      <button onClick={handlePurgeSeat}
                        disabled={purgeLoading || purgeText !== t('ent_seat_purge_phrase')}
                        style={{
                          padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600, color: 'white',
                          background: purgeText === t('ent_seat_purge_phrase') ? '#b91c1c' : '#9ca3af',
                          cursor: purgeText === t('ent_seat_purge_phrase') ? 'pointer' : 'not-allowed',
                        }}>
                        {purgeLoading ? t('ent_seat_purge_loading') : t('ent_seat_purge_cta')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Organisation — montée par Evalens à la livraison du contrat.
                `licenseId` désigne explicitement la licence : un compte Evalens
                n'en a pas à lui, contrairement à un license_admin. */}
            {detailTab === 'org' && (
              <OrganisationEchelons licenseId={selected.id}
                onChanged={() => loadOrgUnits(selected.id)} />
            )}

            {/* Invitations */}
            {detailTab === 'invitations' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                  <button onClick={() => setShowInvite(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <MailPlus size={14} />{t('ent_invite_user')}
                  </button>
                </div>
                {invitations.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>{t('portal_no_invites')}</div>}
                {invitations.map(inv => (
                  <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--gray)', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{inv.email}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {inv.accepted_at ? `Acceptée le ${inv.accepted_at.slice(0, 10)}` : `Expire le ${(inv.expires_at || '').slice(0, 10)}`}
                      </div>
                    </div>
                    {!inv.accepted_at && (
                      <button onClick={() => handleCancelInvite(inv.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--black)' }}>
                        <X size={12} />{t('ent_inv_cancel')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Factures */}
            {detailTab === 'invoices' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
                  <button onClick={() => handleCreateInvoice('regularisation')} disabled={invoiceBusy}
                    style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: invoiceBusy ? 'default' : 'pointer', fontSize: 13, color: 'var(--black)' }}>
                    {t('ent_facture_new_regul')}
                  </button>
                  <button onClick={() => handleCreateInvoice('echeance')} disabled={invoiceBusy}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: invoiceBusy ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <Plus size={14} />{t('ent_facture_new_echeance')}
                  </button>
                </div>

                {invoices.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>{t('ent_facture_empty')}</div>
                )}

                {invoices.map(f => (
                  <div key={f.id} style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--gray)', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {f.type === 'echeance' ? t('ent_facture_type_echeance') : t('ent_facture_type_regul')}
                          {' — '}{f.montant_ttc.toFixed(2)} €
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {t('ent_facture_col_period')} : {f.periode_debut} → {f.periode_fin}
                          {/* Le numéro n'existe qu'après `finalize` : avant, il n'y
                              a rien à afficher, et un tiret vaut mieux qu'un vide. */}
                          {f.qonto_invoice_number ? ` · ${f.qonto_invoice_number}` : ''}
                        </div>
                        {estProforma(f) && (
                          <div style={{ fontSize: 11, color: '#92400e', marginTop: 4 }}>
                            {t('ent_facture_prepared_hint')}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                          background: f.statut === 'cancelled' ? '#fee2e2' : f.statut === 'draft' ? (estProforma(f) ? '#fef3c7' : 'var(--bg-subtle)') : '#dcfce7',
                          color: f.statut === 'cancelled' ? '#991b1b' : f.statut === 'draft' ? (estProforma(f) ? '#92400e' : 'var(--text-muted)') : '#166534',
                        }}>
                          {f.statut === 'draft' && estProforma(f)
                            ? t('ent_facture_status_prepared')
                            : t(`ent_facture_status_${f.statut}`)}
                        </span>

                        {/* Brouillon LOCAL : rien n'existe encore chez QONTO. */}
                        {f.statut === 'draft' && !estProforma(f) && (
                          <>
                            <button onClick={() => handleCancelInvoice(f)} disabled={invoiceBusy}
                              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--black)' }}>
                              {t('ent_facture_cancel')}
                            </button>
                            <button onClick={() => handlePrepareInvoice(f)} disabled={invoiceBusy}
                              style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              {t('ent_facture_prepare')}
                            </button>
                          </>
                        )}

                        {/* Proforma prêt chez QONTO : relisible, encore supprimable. */}
                        {estProforma(f) && (
                          <>
                            <button onClick={() => handleCancelInvoice(f)} disabled={invoiceBusy}
                              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--black)' }}>
                              {t('ent_facture_cancel')}
                            </button>
                            <button onClick={() => handleDownloadInvoice(f)}
                              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--black)' }}>
                              {t('ent_facture_proforma')}
                            </button>
                            {/* Action IRRÉVERSIBLE : elle consomme un numéro de la
                                séquence légale. D'où le rouge, distinct du bleu des
                                actions ordinaires, et la confirmation chiffrée.
                                Elle n'apparaît QU'APRÈS préparation : le document
                                doit avoir pu être relu avant d'être engagé. */}
                            <button onClick={() => handleFinalizeInvoice(f)} disabled={invoiceBusy}
                              style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              {t('ent_facture_send')}
                            </button>
                          </>
                        )}

                        {f.statut !== 'draft' && f.statut !== 'cancelled' && (
                          <button onClick={() => handleDownloadInvoice(f)}
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--black)' }}>
                            {t('ent_facture_pdf')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!showForm && !selected && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
            <Building2 size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <span style={{ fontSize: 14 }}>Sélectionnez une licence ou créez-en une nouvelle</span>
          </div>
        )}
      </div>

      {showInvite && selected && (
        <InviteModal licenseId={selected.id} orgUnits={orgUnits}
          onClose={() => setShowInvite(false)} onSent={rafraichirDetail} />
      )}

      {editSeat && (
        <EditSeatModal user={editSeat} estCabinet={!!selected?.est_cabinet} onSave={handleUpdateSeat} onClose={() => setEditSeat(null)} />
      )}
    </div>
  );
}

// `estCabinet` : les droits d'un siège Cabinet sont ceux du forfait
// (`DROITS_SIEGE_CABINET`) ; le serveur n'y accepte que le rôle et IGNORE le
// reste sans erreur. Les cases sont donc figées, et seul le rôle part.
function EditSeatModal({ user, estCabinet = false, onSave, onClose }) {
  const { t } = useI18n();
  const [role, setRole] = React.useState(user.license_role || 'member');
  const [rights, setRights] = React.useState({
    lic_ocr_enabled: !!user.lic_ocr_enabled,
    lic_projects_enabled: !!user.lic_projects_enabled,
    lic_annotations_enabled: !!user.lic_annotations_enabled,
    lic_ratings_enabled: !!user.lic_ratings_enabled,
  });
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(user.id, estCabinet ? { license_role: role } : { license_role: role, ...rights });
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{t('ent_edit_seat')}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>{user.email}</div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Rôle</label>
          <select value={role} onChange={e => setRole(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--black)' }}>
            <option value="member">{t('ent_role_member')}</option>
            <option value="license_admin">{t('ent_role_admin')}</option>
          </select>
        </div>
        <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>Droits</div>
          {estCabinet && <div style={{ fontSize: 12, color: '#0369a1' }}>{t('ent_rights_cabinet_note')}</div>}
          {[
            ['lic_ocr_enabled', t('ent_rights_ocr')],
            ['lic_projects_enabled', t('ent_rights_projects')],
            ['lic_annotations_enabled', t('ent_rights_annotations')],
            ['lic_ratings_enabled', t('ent_rights_ratings')],
          ].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: estCabinet ? 'default' : 'pointer', opacity: estCabinet ? 0.6 : 1 }}>
              <input type="checkbox" checked={rights[k]} disabled={estCabinet} onChange={e => setRights(r => ({ ...r, [k]: e.target.checked }))} />
              {label}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>{t('cancel')}</button>
          <button onClick={save} disabled={saving}
            style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--blue)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {saving ? '…' : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}
